import { TailwindWrapper } from "@utils/TailwindWrapper";
import {
  Text,
  Button,
  Icon,
  Tooltip,
} from "@ui-component-library";

const baseFeatures = [
  {
    text: "Begränsad skogsbruksplan",
    tooltip:
      "Du får en bra överblick över fastigheten med utvald information per avdelning",
  },
  {
    text: "Begränsad prisjämförelse",
    tooltip:
      "Du får ett bra smakprov på Treebulas unika prisstatistik för virke",
  },
  {
    text: "Sälj virke och biobränsle",
    tooltip:
      "Tillgång till Treebulas marknadsplats för slutavverkning och gallring. Högre konkurrens ger 23% bättre pris",
  },
  {
    text: "Kolkrediter - Se om din skog har potential",
    tooltip:
      "Möjlighet att tjäna pengar på kolkrediter från din skog. Vi visar om du har potential",
  },
  {
    text: "Marknadsinformation",
    tooltip:
      "Treebulas unika och oberoende roll på marknaden ger dig uppdaterad information för bättre affärer",
  },
];

const plusFeatures = [
  {
    text: "Komplett skogsbruksplan",
    tooltip:
      "All data du behöver för ditt skogsägande samlat i ett användarvänligt gränssnitt",
  },
  {
    text: "Komplett prisjämförelse",
    tooltip: "Full tillgång till Treebulas unika prisdatabas för virkespriser",
  },
  {
    text: "Skogsövervakning",
    tooltip:
      "Din skog alltid under uppsikt med Treebulas stallitövervakning av din skog. Skador och risker tydligt presenterat",
  },
  {
    text: "Sälj virke och biobränsle",
    tooltip:
      "Tillgång till Treebulas marknadsplats för slutavverkning och gallring. Högre konkurrens ger 23% bättre pris",
  },
  {
    text: "Kolkrediter - potential, estimerat värde och tjäna kolkrediter",
    tooltip:
      "Möjlighet att tjäna pengar på kolkrediter från din skog. Vi visar hur stor potential just du har",
  },
  {
    text: "Marknadsinformation",
    tooltip:
      "Treebulas unika och oberoende roll på marknaden ger dig uppdaterad information för bättre affärer",
  },
  {
    text: "Fastighetsvärdering",
    tooltip:
      "Uppdaterad prisjämförelse baserad på den senast tillgängliga statistiken från sålda skogsfastigheter",
  },
  {
    text: "Få information när virkespriset förändras",
    tooltip:
      "Vi håller dig uppdaterad så att du kan göra bättre affärer i din skog",
  },
  {
    text: "Virkesvärde per avdelning",
    tooltip:
      "Vår unika statistik ger dig prisinformation per avdelning så att du inte gör dåliga affärer.",
  },
  {
    text: "Rådgivning",
    tooltip:
      "Få stöd av experter som hjälper dig fatta smartare beslut – från virkesförsäljning och digital skogsbruksplan till övervakning, virkespriser och möjligheten att sälja kolkrediter.",
  },
];

const SubscriptionCards = (props) => {
  const getThemeClasses = (isDark) => ({
    checkIcon: isDark ? "fill-white" : "fill-charcoal-500",
    text: isDark ? "text-white-50/70" : "text-gray-600",
    infoIcon: isDark ? "fill-white-50/70" : "fill-gray-600",
  });

  const handleNavigationClick = (href) => {
    window.location.href = href;
  };

  const FeatureList = ({ items, theme = "light" }) => {
    const themeClasses = getThemeClasses(theme === "dark");
    return (
      <ul className="flex flex-col gap-4">
        {items.map((item) => (
          <li
            key={`${theme}-${item.text}`}
            className={"flex items-start justify-between gap-3"}
          >
            <div className="flex flex-row items-start gap-3">
              <Icon
                tw
                name="CheckSimple"
                size="large"
                className={themeClasses.checkIcon}
              />

              <Text
                tw
                variant="text-sm/leading-normal/normal"
                className={themeClasses.text}
              >
                {item.text}
              </Text>
            </div>
            <Tooltip content={item.tooltip}>
              <Icon
                tw
                name="InfoFilled"
                size="large"
                className={`${themeClasses.infoIcon} cursor-pointer`}
              />
            </Tooltip>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <TailwindWrapper>
      <section className="w-full py-5 bg-white container">
        <div className="grid grid-cols-1 gap-6 py-8 lg:grid-cols-[repeat(2,max-content)] lg:auto-rows-fr lg:justify-center">
          <div className="flex h-full w-full max-w-full lg:max-w-[444px] flex-col rounded-xl border border-solid border-[#E5E5E5] bg-white p-8 shadow-xl gap-4">
            <div className="flex flex-col gap-4">
              <Text tw variant="text-lg/leading-normal/semibold">
                Treebula Bas
              </Text>

              <Text
                tw
                variant="text-sm/leading-normal/normal"
                className="text-[#737373]"
              >
                En kort summering av vad du får med Treebula Bas
              </Text>
            </div>
            {props.baseButtonText && (
              <Button
                tw
                variant="primary"
                onClick={() => handleNavigationClick(props.baseButtonLink)}
              >
                {props.baseButtonText}
              </Button>
            )}

            <Text
              tw
              variant="text-sm/leading-normal/medium"
              className="text-charcoal-500"
            >
              Vad som ingår:
            </Text>
            <FeatureList items={baseFeatures} theme="light" />
          </div>

          <div className="flex h-full w-full max-w-full lg:max-w-[444px] flex-col rounded-xl bg-[#0A0A0A] text-white-50 p-8 shadow-xl gap-4">
            <div className="flex flex-col gap-4">
              <Text
                tw
                variant="text-lg/leading-normal/semibold"
                className="text-white-50"
              >
                Treebula Plus
              </Text>
              <Text
                tw
                variant="text-sm/leading-normal/normal"
                className="text-white-50/70"
              >
                En kort summering av vad du får med Treebula Plus
              </Text>
            </div>

            <Button
              tw
              variant="tertiary"
              className="bg-white-50 text-charcoal-500 hover:bg-white-700"
              onClick={() =>
                handleNavigationClick(
                  "/min-skog/?view=overvakning&subscription-checkout=true"
                )
              }
            >
              {props.plusButtonText}
            </Button>

            <Text
              tw
              variant="text-sm/leading-normal/medium"
              className="text-white-50"
            >
              Allt från Treebula Bas, men även:
            </Text>
            <FeatureList items={plusFeatures} theme="dark" />
          </div>
        </div>
      </section>
    </TailwindWrapper>
  );
};

SubscriptionCards.defaultProps = {
  plusButtonText: "Se pris och beställ",
  baseButtonText: undefined,
  baseButtonLink: undefined,
};
SubscriptionCards.fields = {
  plusButtonText: {
    type: "text",
    label: "Plus Button Text",
  },
  baseButtonText: {
    type: "text",
    label: "Base Button Text",
  },
  baseButtonLink: {
    type: "text",
    label: "Base Button Link",
  },
};

export default SubscriptionCards;
